import {
  ApiError,
  NetworkError,
  TimeoutError,
  getErrorMessage,
} from "../utils/errors";
import { ApiEndpoints } from "./apiEndpoints";
import { fetchData, setHeaderOptions } from "./apiHelpers";
import {
  CreateDiagnosisRow,
  DiagnosisRow,
  UpdateDiagnosisRow,
} from "./diagnosis.type";

export const getDiagnosisById = async (
  diagnosisId: string
): Promise<DiagnosisRow> => {
  try {
    const slug = ApiEndpoints.diagnosis + `/${diagnosisId}`;

    const options = await setHeaderOptions({
      method: "GET",
    });

    const diagnosis = await fetchData<DiagnosisRow>(slug, options);

    return diagnosis;
  } catch (error) {
    console.error("Error getting diagnosis by ID:", getErrorMessage(error));
    throw error;
  }
};

export const getDiagnosis = async (
  userId?: string
): Promise<DiagnosisRow[]> => {
  try {
    // Add userId to the URL if provided (for viewing another user's diagnoses)
    const slug =
      ApiEndpoints.diagnosis + "/" + (userId ? `?userId=${userId}` : "");

    const options = await setHeaderOptions({
      method: "GET",
    });

    // Specify a timeout of 10 seconds
    const diagnosis = await fetchData<DiagnosisRow[]>(slug, options, 10000);

    return diagnosis;
  } catch (error) {
    // Log with better context
    if (error instanceof ApiError) {
      console.error(`API Error (${error.status}): ${error.message}`);
    } else if (error instanceof NetworkError) {
      console.error(`Network Error: ${error.message}`);
    } else if (error instanceof TimeoutError) {
      console.error(`Timeout Error: ${error.message}`);
    } else {
      console.error("Error getting diagnosis:", getErrorMessage(error));
    }

    // Rethrow to be handled by the caller
    throw error;
  }
};

export const updateDiagnosis = async (
  diagnosis: UpdateDiagnosisRow,
  patientUserId?: string
): Promise<DiagnosisRow> => {
  try {
    let slug = ApiEndpoints.diagnosis + "/" + diagnosis.id;

    if (patientUserId) {
      slug += `?patient_user_id=${patientUserId}`;
    }

    const options = await setHeaderOptions({
      method: "PUT",
      body: diagnosis,
    });
    const updatedLead = await fetchData<DiagnosisRow>(slug, options);

    return updatedLead;
  } catch (error) {
    throw error;
  }
};

export const createDiagnosis = async (
  diagnosis: CreateDiagnosisRow,
  patientUserId?: string
): Promise<DiagnosisRow> => {
  try {
    let slug = ApiEndpoints.diagnosis + "/";

    if (patientUserId) {
      slug += `?patient_user_id=${patientUserId}`;
    }

    const options = await setHeaderOptions({
      method: "POST",
      body: diagnosis,
    });
    const createdDiagnosis = await fetchData<DiagnosisRow>(slug, options);

    return createdDiagnosis;
  } catch (error) {
    // Log with better context
    if (error instanceof ApiError) {
      console.error(`API Error (${error.status}): ${error.message}`);
    } else if (error instanceof NetworkError) {
      console.error(`Network Error: ${error.message}`);
    } else if (error instanceof TimeoutError) {
      console.error(`Timeout Error: ${error.message}`);
    } else {
      console.error("Error creating diagnosis:", getErrorMessage(error));
    }

    // Rethrow to be handled by the caller
    throw error instanceof Error ? error : new Error(String(error));
  }
};

export const getDiagnosisByUserId = async (
  userId: string
): Promise<DiagnosisRow[]> => {
  try {
    const slug = ApiEndpoints.diagnosis + "/user/" + userId;

    const options = await setHeaderOptions({
      method: "GET",
    });
    const diagnosis = await fetchData<DiagnosisRow[]>(slug, options);

    return diagnosis;
  } catch (error) {
    // Log with better context
    if (error instanceof ApiError) {
      console.error(`API Error (${error.status}): ${error.message}`);
    } else if (error instanceof NetworkError) {
      console.error(`Network Error: ${error.message}`);
    } else if (error instanceof TimeoutError) {
      console.error(`Timeout Error: ${error.message}`);
    } else {
      console.error(
        "Error getting diagnosis by user ID:",
        getErrorMessage(error)
      );
    }

    // Rethrow to be handled by the caller
    throw error instanceof Error ? error : new Error(String(error));
  }
};
